import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const sections = [
  {
    number: '1',
    title: 'Information We Collect',
    content: (
      <>
        <p className="text-gray-600 leading-relaxed mb-4">
          When you use our website or submit a form, we may collect:
        </p>
        <ul className="space-y-3 mb-4">
          {[
            'Name',
            'Email address',
            'Phone number',
            'Property address',
            'Project details you provide',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-checkbox-circle-line text-emerald-600 text-base"></i>
              </div>
              <span className="text-gray-600 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-600 leading-relaxed">
          We may also collect basic website usage data (such as page visits) to improve our site.
        </p>
      </>
    ),
  },
  {
    number: '2',
    title: 'How We Use Your Information',
    content: (
      <>
        <p className="text-gray-600 leading-relaxed mb-4">We use your information to:</p>
        <ul className="space-y-3">
          {[
            'Respond to inquiries',
            'Evaluate your project',
            'Coordinate contractor bids',
            'Communicate with you throughout the process',
            'Improve our services',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-arrow-right-s-line text-emerald-600 text-base"></i>
              </div>
              <span className="text-gray-600 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    number: '3',
    title: 'Sharing Your Information',
    content: (
      <>
        <p className="text-gray-600 leading-relaxed mb-3">
          We only share your information when necessary to provide our service.
        </p>
        <p className="text-gray-600 leading-relaxed mb-3">
          This may include sharing relevant project details with roofing contractors for the purpose of obtaining bids.
        </p>
        <p className="text-gray-600 leading-relaxed font-medium text-gray-700">
          We do <strong>not</strong> sell or rent your personal information to third parties.
        </p>
      </>
    ),
  },
  {
    number: '4',
    title: 'Data Protection',
    content: (
      <p className="text-gray-600 leading-relaxed">
        We take reasonable measures to protect your information from unauthorized access or misuse. However, no method of transmission or storage is completely secure.
      </p>
    ),
  },
  {
    number: '5',
    title: 'Your Choices',
    content: (
      <>
        <p className="text-gray-600 leading-relaxed mb-4">You may contact us at any time to:</p>
        <ul className="space-y-3">
          {[
            'Request access to your information',
            'Ask for corrections',
            'Request deletion of your data',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-checkbox-circle-line text-emerald-600 text-base"></i>
              </div>
              <span className="text-gray-600 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    number: '6',
    title: 'Third-Party Services',
    content: (
      <p className="text-gray-600 leading-relaxed">
        We may use trusted third-party tools (such as email, forms, or payment processors) to operate our business. These providers handle data according to their own privacy policies.
      </p>
    ),
  },
  {
    number: '7',
    title: 'Updates to This Policy',
    content: (
      <p className="text-gray-600 leading-relaxed">
        We may update this Privacy Policy from time to time. Updates will be posted on this page.
      </p>
    ),
  },
  {
    number: '8',
    title: 'Contact Us',
    content: (
      <>
        <p className="text-gray-600 leading-relaxed mb-4">
          If you have any questions about this policy, please contact:
        </p>
        <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
          <p className="font-semibold text-gray-800 mb-1">OverUnder Roofing</p>
          <p className="text-sm text-gray-500 mb-3">C&amp;K Baker Ventures LLC</p>
          <div className="flex flex-col gap-2 text-sm">
            <a href="tel:+19193497701" className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-sm"></i>
              </div>
              (919) 349-7701
            </a>
            <a href="mailto:overunderroofing@gmail.com" className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-line text-sm"></i>
              </div>
              overunderroofing@gmail.com
            </a>
          </div>
        </div>
      </>
    ),
  },
];

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://overunderroofing.com';
    const existing = document.getElementById('privacy-schema');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'privacy-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy – OverUnder Roofing',
      description: 'OverUnder Roofing (C&K Baker Ventures LLC) privacy policy. Learn how we collect, use, and protect your information when you use our roofing bid consultation services in Wake County, NC.',
      url: `${siteUrl}/privacy`,
      isPartOf: { '@type': 'WebSite', name: 'OverUnder Roofing', url: siteUrl },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${siteUrl}/privacy` },
        ],
      },
      publisher: { '@type': 'Organization', name: 'OverUnder Roofing', url: siteUrl },
    });
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('privacy-schema');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* React 19 document metadata */}
      <title>Privacy Policy | OverUnder Roofing – Cary, NC</title>
      <meta name="description" content="OverUnder Roofing (C&K Baker Ventures LLC) privacy policy. Learn how we collect, use, and protect your information when you use our roofing bid consultation services in Wake County, NC." />
      <link rel="canonical" href="https://overunderroofing.com/privacy" />

      {/* Top Nav Strip */}
      <div className="bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
            <i className="ri-arrow-left-line text-base text-emerald-400"></i>
            <span className="text-sm text-gray-300">Back to Home</span>
          </Link>
          <span className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>OverUnder Roofing</span>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">Legal</p>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500">Effective Date: March 25, 2026 &nbsp;·&nbsp; C&amp;K Baker Ventures LLC d/b/a OverUnder Roofing</p>
        </div>
      </div>

      {/* Intro */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-6 mb-10">
          <p className="text-gray-700 leading-relaxed">
            <strong>OverUnder Roofing</strong> (C&amp;K Baker Ventures LLC) respects your privacy. This policy explains what information we collect, how we use it, and how we protect it.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section) => (
            <div key={section.number} className="border border-gray-100 rounded-lg overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-100 px-6 py-4 flex items-center gap-4">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-bold flex-shrink-0">
                  {section.number}
                </span>
                <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">{section.title}</h2>
              </div>
              <div className="px-6 py-5">
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Footer Block */}
        <div className="mt-12 border-t border-gray-200 pt-10">
          <div className="bg-gray-900 text-white rounded-lg p-8 text-center">
            <p className="text-lg font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>OverUnder Roofing</p>
            <p className="text-sm text-gray-400 mb-4">C&amp;K Baker Ventures LLC</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
              <a href="tel:+19193497701" className="text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer">
                (919) 349-7701
              </a>
              <span className="hidden sm:inline text-gray-600">·</span>
              <a href="mailto:overunderroofing@gmail.com" className="text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer">
                overunderroofing@gmail.com
              </a>
            </div>
          </div>
        </div>

        <p className="text-xs text-center text-gray-400 mt-6 mb-10">
          © 2026 OverUnder Roofing · C&amp;K Baker Ventures LLC · Wake County, NC
        </p>
      </div>
    </div>
  );
}
